import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { salvaPromemoriaScadenze } from '../../../../utils/api/promemoria_scadenze_api';
import { reportApiError } from '../../../../utils/reportApiError';
import { formattaData } from '../iot/formatoData';
import CampiInLista from './CampiInLista';
import ConfermaDisattivazioneDialog from './ConfermaDisattivazioneDialog';
import useFormPromemoria from './useFormPromemoria';
import {
  MASSIMO_DESTINATARI,
  MASSIMO_PREAVVISI,
  PREAVVISO_MASSIMO_GIORNI,
  PREAVVISO_PROPOSTO,
  richiestaDaForm,
  spiegazioneSpento,
} from './promemoriaScadenze';

const useStyles = makeStyles((theme) => ({
  finestra: {
    maxWidth: 560,
  },
  contenuto: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  secondario: {
    color: theme.palette.text.secondary,
  },
  preavviso: {
    width: 240,
  },
  azioni: {
    padding: theme.spacing(2, 3, 3),
  },
  // The red Annulla of the app's older dialogs (ModalGiorniChiusura): the
  // brand's secondary colour, with the brand's text colour on it.
  annulla: {
    color: theme.palette.text.main,
    backgroundColor: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
    },
  },
}));

const DESCRIZIONE =
  'Email automatiche ai destinatari quando i documenti di questo esercizio stanno per scadere e il giorno dopo la scadenza.';

const NOTA_PREAVVISI = `Valgono per tutti i documenti dell’esercizio. Da 1 a ${PREAVVISO_MASSIMO_GIORNI} giorni, massimo ${MASSIMO_PREAVVISI}. Predefinito: ${PREAVVISO_PROPOSTO} giorni.`;

const NOTA_PREAVVISI_AL_MASSIMO = `Hai raggiunto il massimo di ${MASSIMO_PREAVVISI} preavvisi. Valori ammessi: da 1 a ${PREAVVISO_MASSIMO_GIORNI} giorni.`;

const NOTA_DESTINATARI = `Massimo ${MASSIMO_DESTINATARI} destinatari. Ogni destinatario riceve un’email personale, senza CC.`;

/**
 * The "Promemoria scadenze documenti" dialog of Documenti (PRD "Invio email
 * scadenze documenti" v2.0, BSS-1968, Figma "BSS-1089 · Documenti · Modale
 * Promemoria aperta"). It is mounted only while open, so Annulla throws the
 * edits away with it.
 *
 * Errors on the fields are shown on the fields and announced with the
 * section's snackbar; a refusal of the server goes to the app's error dialog
 * and leaves the dialog open with what was typed. Switching off a service that
 * was on asks first: from then on no expiry email leaves for the esercizio.
 * While a save is in flight the dialog does not close, Esc and backdrop
 * included: its answer still has to land on it.
 */
const PromemoriaScadenzeDialog = ({
  esercizioId,
  configurazione,
  onChiudi,
  onSalvata,
  onAvviso,
}) => {
  const classes = useStyles();
  const { form, errori, cambiaAttivo, preavvisi, destinatari, valori, valida } =
    useFormPromemoria(configurazione);
  const [inCorso, setInCorso] = useState(false);
  const [confermaDisattivazione, setConfermaDisattivazione] = useState(false);

  const invia = () => {
    setInCorso(true);
    salvaPromemoriaScadenze(
      esercizioId,
      richiestaDaForm(valori(), configurazione),
    )
      .then((salvata) => {
        setInCorso(false);
        onSalvata(salvata);
      })
      .catch((errore) => {
        setInCorso(false);
        reportApiError(errore);
      });
  };

  const salva = () => {
    if (!valida()) {
      onAvviso('Correggi i campi evidenziati prima di salvare', 'error');
      return;
    }
    if (configurazione.attivo && !form.attivo) {
      setConfermaDisattivazione(true);
      return;
    }
    invia();
  };

  const attivoDal = form.attivo && formattaData(configurazione.attivatoIl);

  return (
    <Dialog
      open
      onClose={() => !inCorso && onChiudi()}
      fullWidth
      classes={{ paper: classes.finestra }}
      aria-labelledby="promemoria-scadenze-titolo"
    >
      <DialogTitle id="promemoria-scadenze-titolo">
        Promemoria scadenze documenti
      </DialogTitle>
      <DialogContent className={classes.contenuto}>
        <Typography
          variant="body2"
          component="p"
          className={classes.secondario}
        >
          {DESCRIZIONE}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={form.attivo}
              onChange={(event) => cambiaAttivo(event.target.checked)}
              color="primary"
            />
          }
          label="Attivo per questo esercizio"
        />
        {attivoDal && configurazione.attivo && (
          <Typography
            variant="body2"
            component="p"
            className={classes.secondario}
          >
            {`Attivo dal ${attivoDal}`}
          </Typography>
        )}
        {!form.attivo && (
          <Typography
            variant="body2"
            component="p"
            className={classes.secondario}
          >
            {spiegazioneSpento(valori())}
          </Typography>
        )}
        {form.attivo && (
          <>
            <CampiInLista
              titolo="Preavvisi (giorni prima della scadenza)"
              etichettaAggiungi="Aggiungi preavviso"
              etichettaRimuovi="Rimuovi preavviso"
              righe={form.preavvisi}
              massimo={MASSIMO_PREAVVISI}
              nota={
                form.preavvisi.length >= MASSIMO_PREAVVISI
                  ? NOTA_PREAVVISI_AL_MASSIMO
                  : NOTA_PREAVVISI
              }
              onAggiungi={preavvisi.aggiungi}
              onRimuovi={preavvisi.rimuovi}
              campo={(riga, indice) => (
                <TextField
                  id={`promemoria-preavviso-${riga.id}`}
                  className={classes.preavviso}
                  label="Giorni prima della scadenza"
                  type="number"
                  value={riga.valore}
                  onChange={(event) =>
                    preavvisi.cambia(indice, event.target.value)
                  }
                  error={Boolean(errori.preavvisi[riga.id])}
                  helperText={errori.preavvisi[riga.id]}
                  inputProps={{
                    min: 1,
                    max: PREAVVISO_MASSIMO_GIORNI,
                    step: 1,
                  }}
                  margin="dense"
                />
              )}
            />
            <CampiInLista
              titolo="Destinatari email"
              etichettaAggiungi="Aggiungi recapito"
              etichettaRimuovi="Rimuovi recapito"
              righe={form.destinatari}
              massimo={MASSIMO_DESTINATARI}
              nota={NOTA_DESTINATARI}
              onAggiungi={destinatari.aggiungi}
              onRimuovi={destinatari.rimuovi}
              campo={(riga, indice) => (
                <TextField
                  id={`promemoria-destinatario-${riga.id}`}
                  type="email"
                  fullWidth
                  placeholder="nome@esempio.it"
                  value={riga.valore}
                  onChange={(event) =>
                    destinatari.cambia(indice, event.target.value)
                  }
                  error={Boolean(errori.destinatari[riga.id])}
                  helperText={errori.destinatari[riga.id]}
                  inputProps={{
                    'aria-label': `Email destinatario ${indice + 1}`,
                  }}
                  margin="dense"
                />
              )}
            />
          </>
        )}
      </DialogContent>
      <DialogActions className={classes.azioni}>
        <Button
          type="button"
          variant="contained"
          className={classes.annulla}
          onClick={onChiudi}
          disabled={inCorso}
        >
          Annulla
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          onClick={salva}
          disabled={inCorso}
        >
          Salva
        </Button>
      </DialogActions>
      <ConfermaDisattivazioneDialog
        aperta={confermaDisattivazione}
        onAnnulla={() => setConfermaDisattivazione(false)}
        onConferma={() => {
          setConfermaDisattivazione(false);
          invia();
        }}
      />
    </Dialog>
  );
};

PromemoriaScadenzeDialog.propTypes = {
  esercizioId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  configurazione: PropTypes.shape({
    attivo: PropTypes.bool.isRequired,
    preavvisi: PropTypes.arrayOf(PropTypes.number).isRequired,
    destinatari: PropTypes.arrayOf(PropTypes.string).isRequired,
    attivatoIl: PropTypes.string,
  }).isRequired,
  onChiudi: PropTypes.func.isRequired,
  onSalvata: PropTypes.func.isRequired,
  onAvviso: PropTypes.func.isRequired,
};

export default PromemoriaScadenzeDialog;
